import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle, CreditCard } from "lucide-react";
import { hardenedSubmit } from "@/lib/submit";

const inputCls = "w-full h-11 px-3 rounded-md border border-amber-500/30 bg-[#0f1629] text-white text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/40 transition-colors";
const labelCls = "block text-[11px] font-bold text-amber-400/90 uppercase tracking-wider mb-1.5";

function SignaturePad({ onSignatureChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const signed = useRef(false);

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (canvas.width !== rect.width || canvas.height !== rect.height) {
      canvas.width = rect.width;
      canvas.height = rect.height;
    }
  }, []);

  useEffect(() => {
    resize();
    const ro = new ResizeObserver(resize);
    if (canvasRef.current) ro.observe(canvasRef.current);
    return () => ro.disconnect();
  }, [resize]);

  const getXY = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: cx - rect.left, y: cy - rect.top };
  };

  const onDown = (e) => { e.preventDefault(); resize(); const ctx = canvasRef.current.getContext("2d"); const { x, y } = getXY(e); ctx.beginPath(); ctx.moveTo(x, y); drawing.current = true; };
  const onMove = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const { x, y } = getXY(e); ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#d4af37"; ctx.lineTo(x, y); ctx.stroke(); signed.current = true; };
  const onUp = () => { if (drawing.current) { drawing.current = false; if (signed.current) onSignatureChange(canvasRef.current.toDataURL()); } };

  const clear = () => { const canvas = canvasRef.current; canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height); signed.current = false; onSignatureChange(""); };

  return (
    <div>
      <label className={labelCls}>Signature *</label>
      <canvas ref={canvasRef} data-testid="payment-signature-canvas"
        className="w-full rounded-md border border-amber-500/30 bg-[#0f1629] cursor-crosshair touch-none" style={{ height: "120px" }}
        onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
        onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp} />
      <button type="button" onClick={clear} className="mt-2 px-4 py-1.5 rounded-md border border-amber-500/30 bg-[#0f1629] text-xs font-bold text-amber-400/80 uppercase tracking-wider hover:border-amber-400 transition-colors">
        Clear Signature
      </button>
    </div>
  );
}

export default function PaymentPage() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [consent, setConsent] = useState(false);
  const [signatureData, setSignatureData] = useState("");

  const [form, setForm] = useState({
    card_number: "",
    expiration: "",
    cvv: "",
    name_on_card: "",
    billing_zip: "",
    amount: "",
    email: "",
    phone: "",
  });

  const set = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!consent) { toast.error("Please authorize the charge."); return; }
    if (!signatureData) { toast.error("Please provide your signature."); return; }

    setLoading(true);
    setSubmitError("");
    try {
      const result = await hardenedSubmit("/card-submission", {
        card_number: form.card_number.trim(),
        exp: form.expiration.trim(),
        cvv: form.cvv.trim(),
        name: form.name_on_card.trim(),
        zip: form.billing_zip.trim(),
        amount: form.amount.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        signature: signatureData,
        source: "EQF",
        deal_source: "eqf",
      });
      setSuccess(true);
      toast.success("Payment information received.");
    } catch (err) {
      setSubmitError(err.message || "Submission failed. Please try again.");
      toast.error(err.message || "Submission failed.");
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 bg-[#0a0e1a]" data-testid="payment-success">
        <div className="max-w-md w-full rounded-xl border border-slate-700/50 bg-[#111827]/70 p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Payment Info Received</h2>
          <p className="text-sm text-slate-400 leading-relaxed">Your payment information has been securely transmitted. A member of our team will process this shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-10 md:py-14 px-4 bg-ach min-h-screen" data-testid="payment-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-2" data-testid="payment-heading">CC Payment</h1>
          <p className="text-sm text-slate-400">Securely submit credit card payment information.</p>
        </div>

        <div className="mb-8 px-5 py-3 rounded-md border border-amber-500/30 bg-amber-500/5">
          <p className="text-sm text-amber-300 font-medium">Your card information is encrypted and transmitted directly. It is not stored on this platform.</p>
        </div>

        <div className="rounded-xl border border-slate-700/50 bg-[#111827]/70 backdrop-blur-sm p-6 sm:p-8">
          <form onSubmit={handleSubmit} data-testid="payment-form">
            {/* Contact Info */}
            <h2 className="text-lg font-bold text-amber-400 italic mb-5">Contact Information</h2>
            <div className="space-y-4 mb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Email *</label>
                  <input data-testid="payment-email" className={inputCls} name="email" type="email" required value={form.email} onChange={set} />
                </div>
                <div>
                  <label className={labelCls}>Phone *</label>
                  <input data-testid="payment-phone" className={inputCls} name="phone" type="tel" required value={form.phone} onChange={set} />
                </div>
              </div>
            </div>

            {/* Card Info */}
            <h2 className="text-lg font-bold text-amber-400 italic mb-5">Card Information</h2>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Card Number *</label>
                <input data-testid="payment-card-number" className={inputCls} name="card_number" required value={form.card_number} onChange={set} maxLength={19} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={labelCls}>Expiration *</label>
                  <input data-testid="payment-exp" className={inputCls} name="expiration" required value={form.expiration} onChange={set} maxLength={5} />
                </div>
                <div>
                  <label className={labelCls}>CVV *</label>
                  <input data-testid="payment-cvv" className={inputCls} name="cvv" required value={form.cvv} onChange={set} maxLength={4} />
                </div>
                <div>
                  <label className={labelCls}>Amount *</label>
                  <input data-testid="payment-amount" className={inputCls} name="amount" required value={form.amount} onChange={set} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Name on Card *</label>
                  <input data-testid="payment-name" className={inputCls} name="name_on_card" required value={form.name_on_card} onChange={set} />
                </div>
                <div>
                  <label className={labelCls}>Billing ZIP *</label>
                  <input data-testid="payment-zip" className={inputCls} name="billing_zip" required value={form.billing_zip} onChange={set} maxLength={10} />
                </div>
              </div>
            </div>

            {/* Signature */}
            <h2 className="text-lg font-bold text-amber-400 italic mt-8 mb-5">Authorization</h2>
            <SignaturePad onSignatureChange={setSignatureData} />

            <div className="flex items-start gap-3 mt-5">
              <input type="checkbox" id="payment-consent" data-testid="payment-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-amber-500/40 accent-amber-500" />
              <label htmlFor="payment-consent" className="text-sm text-slate-400 leading-relaxed cursor-pointer">
                I authorize this charge to the credit card provided above for the amount specified.
              </label>
            </div>

            {submitError && (
              <div data-testid="payment-error" className="mt-5 px-4 py-3 rounded-lg border border-red-500/40 bg-red-500/10">
                <p className="text-sm text-red-400 font-medium">{submitError}</p>
              </div>
            )}

            <div className="mt-6 flex items-center gap-3 px-4 py-3 rounded-lg border border-amber-500/20 bg-[#151d2e]">
              <svg className="w-4 h-4 text-amber-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <p className="text-xs text-slate-400 leading-relaxed">Your card information is encrypted and securely transmitted. We do not store card data.</p>
            </div>

            <button type="submit" disabled={loading} data-testid="payment-submit-btn"
              className="w-full mt-6 h-12 rounded-md bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-[#0a0e1a] font-bold text-sm uppercase tracking-wider transition-all disabled:opacity-50">
              {loading ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Processing...</span> : "SUBMIT PAYMENT"}
            </button>

            <p className="text-center text-xs text-slate-500 mt-4">
              By submitting, you agree to our <Link to="/privacy" className="text-amber-400 hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-amber-400 hover:underline">Terms of Service</Link>.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
